import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { AuthMode, AuthState } from '@common/components/login/enums';
import { BrowserStorage, RoutePath } from '@common/enums';
import { RestoreToStorage, SignInAsAdmin } from '@common/models';
import { AnotherAccountResponse, AuthCallbackResult, AuthResponse, AuthUrlOData } from '@common/models/auth/auth.model';
import { LocalStorageService, QueryParamsService, SessionStorageService } from '@common/services';
import { AuthSessionService } from '@common/services/auth/auth-session.service';
import { AuthStateService } from '@common/services/auth/auth-state.service';
import { AuthService } from '@common/services/auth/auth.service';
import { ConfigurationService } from '@common/services/configuration.service';
import { getErrorText } from '@common/utils';
import { WINDOW } from '@common/window.provider';
import { I18NextPipe } from 'angular-i18next';
import { LoaderComponent } from 'mbs-ui-kit';
import { from, ignoreElements, Observable, of, switchMap, throwError } from 'rxjs';
import { catchError, filter, tap } from 'rxjs/operators';

@Component({
  imports: [LoaderComponent],
  template: `<mbs-loader />`,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AuthCallbackComponent implements OnInit {
  private readonly authService = inject(AuthService);
  private readonly authSession = inject(AuthSessionService);
  private readonly authState = inject(AuthStateService);
  private readonly config = inject(ConfigurationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly jwtService = inject(JwtHelperService);
  private readonly i18nPipe = inject(I18NextPipe);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly queryParamsService = inject(QueryParamsService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly sessionStorageService = inject(SessionStorageService);
  private readonly window = inject(WINDOW);

  ngOnInit(): void {
    this.checkAndRedirectToHost();
  }

  private sendAuthCallback(): void {
    const code = this.queryParamsService.getParam('code');
    const state = this.queryParamsService.getParam('state');

    if (!code || !state) {
      console.error('Caught error manually: Missing required query params');
      return this.authService.logout();
    }

    const payload = { Code: code, State: state };

    this.authService
      .authCallback(payload)
      .pipe(
        filter(Boolean),
        switchMap((res) => this.stateProcessing(res)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (response) => {
          if (response.Mode === AuthMode.AnotherAccount) {
            const restoreToFrom: RestoreToStorage = this.sessionStorageService.get(BrowserStorage.RestoreToAnotherUser);
            const url = this.router.createUrlTree([restoreToFrom.redirectUrl], {
              relativeTo: this.route
            });

            this.updateRestoreToAnotherUserSessionStorage(restoreToFrom.redirectUrl, response);
            void this.router.navigateByUrl(url);
          } else if (response.Mode === AuthMode.SecondSignIn && this.sessionStorageService.get(BrowserStorage.RestoreFileTeams)) {
            const restoreToFrom: SignInAsAdmin = this.sessionStorageService.get(BrowserStorage.RestoreFileTeams);
            const url = this.router.createUrlTree([restoreToFrom.redirectUrl], {
              relativeTo: this.route
            });

            void this.router.navigateByUrl(url);
          } else {
            const prefix = this.authService.getPath(response as AuthResponse);
            const redirectPage = this.queryParamsService.getParam('redirectPage');
            const page = redirectPage ? RoutePath[redirectPage] : '';

            this.localStorageService.set<string>(BrowserStorage.AuthPrefix, prefix);
            this.localStorageService.set<string>(BrowserStorage.Token, (response as AuthResponse).Token);
            this.authSession.setAuthenticated(true);

            void this.router.navigate([prefix + page]);
          }
        },
        error: (res: HttpErrorResponse | AuthCallbackResult) => this.authService.logout(this.normalizeError(res))
      });
  }

  private normalizeError(res: HttpErrorResponse | AuthCallbackResult): HttpErrorResponse | undefined {
    if ('State' in res && res.State === AuthState.ProviderServiceUnavailable) {
      const message = this.config.get('production')
        ? this.i18nPipe.transform('common.unavailableMBSService.production', { format: 'capitalize' })
        : this.i18nPipe.transform('common.unavailableMBSService.development', { format: 'capitalize' });

      return new HttpErrorResponse({ status: 440, error: { message } });
    } else {
      return res instanceof HttpErrorResponse ? res : undefined;
    }
  }

  private checkAndRedirectToHost(): void {
    try {
      const sessionId = JSON.parse(this.jwtService.urlBase64Decode(this.queryParamsService.getParam('state')))?.loginSessionId;

      this.authService
        .getHost(sessionId)
        .pipe(
          catchError((res) => {
            if (res?.status === 404) {
              const custom = {
                State: AuthState.SuspiciousActivityDetected,
                Model: { ErrorDescription: getErrorText(res) }
              } as AuthResponse;

              return this.stateProcessing(custom);
            } else {
              return throwError(() => res);
            }
          }),
          tap((response) => {
            const { value: host } = response || {};

            if (this.window.location.host !== host) {
              const url = new URL(
                this.window.location.protocol + '//' + host.toLowerCase() + this.window.location.pathname + this.window.location.search
              );

              this.window.location.assign(url);
            } else {
              this.sendAuthCallback();
            }
          }),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe();
    } catch (e) {
      throw new Error('Caught error manually: Cannot decode base64 data');
    }
  }

  private updateRestoreToAnotherUserSessionStorage(route: string, data: AuthResponse | AnotherAccountResponse): void {
    this.sessionStorageService.set(BrowserStorage.RestoreToAnotherUser, { route, data });
  }

  private stateProcessing(res: AuthCallbackResult): Observable<any> {
    const { State, Model } = res || {};

    switch (State) {
      case AuthState.Success:
        return of(res);
      case AuthState.Approve:
      case AuthState.AccessDenied:
      case AuthState.AccessRequest:
      case AuthState.Blocked:
      case AuthState.DomainNotFound:
      case AuthState.NotGrant:
      case AuthState.ServiceUnavailable:
      case AuthState.SignInDisabled:
      case AuthState.SuspiciousActivityDetected:
      case AuthState.UserAccountDisabled: {
        this.authState.patchField('modelBasel', Model);

        return from(this.router.navigate([RoutePath.Account], { queryParams: { state: State } })).pipe(ignoreElements());
      }
      case AuthState.Redirect: {
        const { RedirectUrl } = (res as AuthUrlOData) || {};

        if (RedirectUrl) {
          return from(this.window.open(RedirectUrl, '_self')).pipe(ignoreElements());
        } else {
          throw new Error(`Caught error manually: State "${State}". Not found redirect url`);
        }
      }
      case AuthState.AuthCodeInvalid:
      case AuthState.TokenInvalid:
      default:
        return throwError(() => res);
    }
  }
}
